<template>
  <div>
    <TopSection class="my-3" :title="$t('uploads') | capitalize">
      <!-- Status Summary -->
      <template v-slot:status>
        <div class="mr-3 align-self-start">
          <Status
            :name="statusTypes.PENDING"
            :count="fileStates[statusTypes.PENDING] || 0"
            :text="$t('pending') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
            clickable
            :active="selectedStatus == statusTypes.PENDING"
            @click="onStatusClick"
          />
          <Status
            :name="statusTypes.PROCESSING"
            :count="fileStates[statusTypes.PROCESSING] || 0"
            :text="$t('processing') | capitalize"
            :icon="['fas', 'question-circle']"
            color="orange"
            clickable
            :active="selectedStatus == statusTypes.PROCESSING"
            @click="onStatusClick"
          />
        </div>
        <div class="mr-3 align-self-start">
          <Status
            :name="statusTypes.INGESTED"
            :count="fileStates[statusTypes.INGESTED] || 0"
            :text="$t('ingested') | capitalize"
            :icon="['fas', 'check-circle']"
            color="green"
            clickable
            :active="selectedStatus == statusTypes.INGESTED"
            @click="onStatusClick"
          />
          <Status
            :name="statusTypes.ERROR"
            :count="fileStates[statusTypes.ERROR] || 0"
            :text="$t('error') | capitalize"
            :icon="['fas', 'exclamation-circle']"
            color="red"
            clickable
            :active="selectedStatus == statusTypes.ERROR"
            @click="onStatusClick"
          />
          <Status
            :count="''"
            :text="$t('clear') | capitalize"
            :icon="['fas', 'times']"
            clickable
            :active="selectedStatus == ''"
            @click="onStatusClick('')"
          />
        </div>
      </template>

      <!-- Actions -->
      <template v-slot:actions>
        <b-button
          size="lg"
          variant="primary"
          :href="$config.SOUNDSYS_TEMPLATE_URL"
        >
          {{ $t('downloadTemplate') | capitalize }}
        </b-button>
      </template>

      <!-- Filters -->
      <template v-slot:filters>
        <DatePicker
          v-model="dateRange"
          :placeholder="$t('select_date_range') | capitalize"
          class="ml-3"
          range
        />
        <FilterModule
            :title="$t('filterFiles') | capitalize"
            :fileTypes="{
              [fileTypes.SOUNDSYS]: 'SoundSys Template',
              [fileTypes.DDEX_1_3]: 'DDEX MLC 1.3',
              [fileTypes.DDEX_1_4]: 'DDEX MLC 1.4',
            }"
            :table="$refs.repertoireFiles"
        />
      </template>
    </TopSection>

    <v-server-table
      v-if="dataReady"
      ref="repertoireFiles"
      url="repertoirefiles/"
      :columns="soundrecordingFilesColumns"
      :options="options"
      @pagination="onPagination"
      @sorted="onSorted"
      @filter="onFilter"
    >
      <template v-slot:filename="{ row }">
        <div
          v-b-tooltip.hover
          :title="row.filename"
          :class="row.status === statusTypes.INGESTED ? 'link-class' : 'text-class'"
          @click="selectRow(row)"
        >
          {{ row.filename }}
        </div>
        <div class="table-sub-class text-sm">
          {{ row.uploader }}
        </div>
      </template>

      <!-- STATUS COLUMN -->
      <template v-slot:status="{ row }">
        <Status
          v-if="row.status == statusTypes.PENDING"
          :text="$t('pending') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.PROCESSING"
          :text="$t('processing') | capitalize"
          :icon="['fas', 'question-circle']"
          color="orange"
        />
        <Status
          v-if="row.status == statusTypes.INGESTED"
          :text="$t('ingested') | capitalize"
          :icon="['fas', 'check-circle']"
          color="green"
        />
        <Status
          v-if="row.status == statusTypes.ERROR"
          :text="$t('error') | capitalize"
          :icon="['fas', 'exclamation-circle']"
          color="red"
          :active="true"
          @click="showErrors(row, true)"
          clickable
        />
      </template>

      <!-- LANDING DB STATUS -->
      <template v-slot:ingestionStats="{ row }">
        <!-- Row errors -->
        <div v-if="row.stats && row.stats.landing_db_ingestion">
          <div
            v-for="item in ['total_count','ingested_count','error_count']"
            :key="item"
            class="table-sub-class text-sm inline"
          >
            <div class="mb-1">
              {{ getStatsDisplay(item) | capitalize }}: {{ row.stats.landing_db_ingestion | get([item], '') }}
              <span class="clickable color-blue">
                <i
                  v-if="item == 'error_count' && row.stats.landing_db_ingestion.error_count > 0"
                  class="fas fa-plus-circle"
                  @click="showErrors(row)"
                />
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- SINGLE VIEW STATUS -->
      <template v-slot:matchingStats="{ row }">
        <div v-if="row.stats && row.stats.single_view_ingestion">
          <div
            v-for="k in ['total_unique_count','linked_count','created_count','manually_linked_count','manually_created_count','pending_for_validation_count']"
            :key="k"
            class="table-sub-class text-sm inline"
          >
            <div>{{ getStatsDisplay(k) | capitalize }}: {{ row.stats.single_view_ingestion[k] }}</div>
          </div>
        </div>
      </template>

      <!-- UPLOAD DATE COLUMN -->
      <template v-slot:upload_date="{ row }">
        <div class="table-main-class">
          {{ row.upload_date | transformDate("MMMM Do YYYY") }}
        </div>
        <div class="table-sub-class text-sm">
          {{ row.upload_date | transformDate("h:mm a") }}
        </div>
      </template>

      <!-- ORGANIZATION COLUMN -->
      <template v-slot:organization="{ row }">
        <div class="d-flex table-main-class">
          <div class="table-logo">
            <img
              width="20"
              src="@/assets/img/mlc.png"
            >
          </div>
          {{ row.organization }}
        </div>
      </template>
    </v-server-table>

    <b-modal
      id="errorsModal"
      ref="errorsModal"
      hide-footer
      hide-header
      size="xl"
    >
      <FileErrorsTable
        :key="currentFile | get('id')"
        :item="currentFile"
        module="repertoirefiles"
        :onlyShowFileLevelErrors="onlyShowFileLevelErrors"
      />
    </b-modal>
  </div>
</template>

<script>
import { capitalize, get } from "lodash"
import {
  doRequest,
  responsePaginatedAdapter
} from "@/assets/libs/utils"
import { mapGetters, mapState } from "vuex"
import { listRouteMixin } from "@/utils/mixins"
import moment from "moment"


export default {
  name: "RepertoireFilesList",
  mixins: [listRouteMixin],
  watch: {
    dateRange (val) {
      this.start_date = this.parse_date(val[0])
      this.end_date = this.parse_date(val[1])
      this.$router.replace({
        name: "repertoire-files",
        query: { from: this.start_date, to: this.end_date, page: 1 }
      })
    }
  },
  methods: {
    showErrors (row, onlyShowFileLevelErrors=false) {
      this.currentFile = row
      this.onlyShowFileLevelErrors = onlyShowFileLevelErrors
      this.$refs.errorsModal.show()
    },
    parse_date (val) {
      return !val ? "" : this.$options.filters.moment(val).format("YYYY-MM-DD")
    },
    getStatsDisplay (key) {
      const mapToDisplay = {
        created_count: this.$t("created"),
        error: this.$t("error"),
        error_count: this.$t("errors"),
        ingested: this.$t("ingested"),
        ingested_count: this.$t("ingested"),
        linked_count: this.$t("linked"),
        manually_linked_count: this.$t("manually_linked"),
        manually_created_count: this.$t("manually_created"),
        pending_for_validation_count: this.$t("pending_for_validation"),
        total_unique_count: this.$t("total_unique"),
        total_count: this.$t("total"),
      }
      return get(mapToDisplay, key, key)
    },
    selectRow (row) {
      if (row.status !== this.statusTypes.INGESTED) return
      this.$router.push({
        name: "repertoire-file-detail",
        params: {
          id: row.id
        }
      })
    },
    getData () {
      this.$refs.repertoireFiles.getData()
    },
    onStatusClick (status) {
      const { name, query } = this.$route
      const queryParams = { ...query, status }
      this.selectedStatus = status

      if (!status) delete queryParams.status

      this.$router.replace({
        name: name,
        query: { ...queryParams }
      })
    },
    onFilter (search) {
      this.options.previousPage = null
      this.$router.replace({ query: { ...this.$route.query, fuzzy_search: search, page: 1 } })
    },
    exportUrl () {
      let table = this.$refs.sounrecordingstable.getTableRef()
      let url =
        this.$http.defaults.baseURL +
        table.options.csv_url.replace("doc/", "/")
      return url
    }
  },
  computed: {
    ...mapGetters("soundsys", ["statusTypes", "fileTypes"]),
    ...mapState("soundsys", ["dataReady"]),
  },
  mounted () {
    // set initial values from url parameters
    const from = get(this.$route.query, "from", "")
    const to = get(this.$route.query, "to", "")

    const fromIsValid = moment(from, "YYYY-MM-DD", true).isValid()
    const toIsValid = moment(to, "YYYY-MM-DD", true).isValid()

    this.selectedStatus = this.$route.query?.status

    if(fromIsValid && toIsValid) {
      const fromDate = new Date(from)
      const toDate = new Date(to)

      this.dateRange = [fromDate, toDate]
    }
  },
  data () {
    return {
      dateRange: "",
      start_date: "",
      end_date: "",
      currentFile: null,
      onlyShowFileLevelErrors: false,
      fileStates: {},
      count: 0,
      selectedStatus: "",
      soundrecordingFilesColumns: [
        "filename",
        "file_type",
        "upload_date",
        "organization",
        "status",
        "ingestionStats",
        "matchingStats"
      ],
      csv_url: "",
      options: {
        sortable: [
          "filename",
          "file_type",
          "upload_date",
          "updated",
          "organization",
          "status"
        ],
        filterable: true,
        headings: {
          processed_url: capitalize(this.$t("fileName")),
          file_type: capitalize(this.$t("fileType")),
          upload_date: capitalize(this.$t("uploadDate")),
          organization: capitalize(this.$t("memberSociety")),
          status: capitalize(this.$t("status")),
          ingestionStats: capitalize(this.$t("ingestionStats")),
          matchingStats: capitalize(this.$t("matchingStats"))
        },
        columnsClasses: {
          processed_url: "width-large",
        },
        mapOrdering (params) {
          if (!params.ordering) params.ordering = "-upload_date"

          if (params.ordering.match(/organization/)) {
            params.ordering = params.ordering.replace(
              "organization",
              "uploader__member__username"
            )
          }
          return params
        },
        requestFunction (params) {
          let queryParams = { ...params, ...this.$route.query }
          queryParams = this.options.mapOrdering(queryParams)

          this.page = Number(get(queryParams, "page", 1))

          return doRequest(this.url, queryParams)
        },
        responseAdapter: resp => {
          this.fileStates = resp.data.states
          this.count = resp.data.count
          return responsePaginatedAdapter(resp)
        }
      }
    }
  }
}
</script>
